import { SortDirection } from "@episerver/ui-framework";
import { createSlice } from "@reduxjs/toolkit";
import {
    ConfirmDialog,
    DeleteMode,
    Organization,
    PaginationModel,
    ServerSortType,
    SortModel,
} from "../model/organization";
import { deleteOrganizations, searchOrganizations } from "./asyncThunks";

const getData = (data: any) => {
    const { organizations, totalRecords, query, sortField, sortType, startIndex, recordsToRetrieve } = data;
    return {
        organizations: organizations,
        keyword: query,
        pagination: {
            startRow: startIndex,
            rowsPerPage: recordsToRetrieve,
            totalRowCount: totalRecords,
            rowsPerPageOptions: initialOrganizationState.pagination.rowsPerPageOptions,
        },
        sort: {
            sortedColumn: sortField,
            sortDirection: sortType === ServerSortType.Asc ? SortDirection.Ascending : SortDirection.Descending,
        },
    };
};

export const initialOrganizationState = {
    keyword: "",
    organizations: [] as Organization[],
    selectedOrganizations: [] as string[],
    sort: {
        sortedColumn: "Name",
        sortDirection: SortDirection.Ascending,
    } as SortModel,
    pagination: {
        startRow: 0,
        rowsPerPage: 25,
        rowsPerPageOptions: [25, 50, 100],
        totalRowCount: 0,
    } as PaginationModel,
    confirmDialog: {
        open: false,
        data: {
            mode: DeleteMode.Detach,
            organizationIds: [],
        },
    } as ConfirmDialog,
};

export const organizationListSlice = createSlice({
    name: "organizationList",
    initialState: initialOrganizationState,
    reducers: {
        selectOrganization: (state, action) => {
            state.selectedOrganizations.push(action.payload);
        },
        deselectOrganization: (state, action) => {
            state.selectedOrganizations = state.selectedOrganizations.filter(
                (organizationId: string) => organizationId !== action.payload
            );
        },
        selectAllOrganizations: (state) => {
            state.selectedOrganizations = state.selectedOrganizations
                .concat(state.organizations.map((organization: Organization) => organization.primaryKeyId!))
                .filter(function (elem, index, self) {
                    return index === self.indexOf(elem);
                });
        },
        deselectAllOrganizations: (state) => {
            state.selectedOrganizations = state.selectedOrganizations.filter(function (el) {
                return !state.organizations
                    .map((organization: Organization) => organization.primaryKeyId!)
                    .includes(el);
            });
        },
        sort: (state, action): void => {
            const { column, direction } = action.payload;
            state.sort = { sortDirection: direction, sortedColumn: column };
        },
        onChangeConfirmDialog: (state, action): void => {
            state.confirmDialog = action.payload;
        },
        reset: (state): void => {
            state.selectedOrganizations = initialOrganizationState.selectedOrganizations;
        },
    },
    extraReducers: {
        [`${searchOrganizations.fulfilled}`]: (state, action): void => {
            const data = getData(action.payload);
            Object.keys(data).forEach((key) => {
                state[key as keyof typeof state] = data[key as keyof typeof data];
            });
        },
        [`${deleteOrganizations.fulfilled}`]: (state, action): void => {
            const data = getData(action.payload);
            Object.keys(data).forEach((key) => {
                state[key as keyof typeof state] = data[key as keyof typeof data];
            });
            state.selectedOrganizations = [];
        },
    },
});

// Actions
export const {
    selectOrganization,
    deselectOrganization,
    selectAllOrganizations,
    deselectAllOrganizations,
    sort,
    onChangeConfirmDialog,
    reset,
} = organizationListSlice.actions;
